import {
  Box,
  Button,
  DropdownOption,
  TextField,
  ThemeOverride
} from "@modernatx-internal/component-library";
import { useRouter } from "next/router";
import React from "react";

import { useExperience } from "@/context/ExperienceContext";
import { useLocationSearch } from "@/finder/hooks/V2/useLocationSearch";

import { ContentComponentProps, FinderSearchBarContent } from "../types";

const SearchButton = React.memo(({ onClick }: { onClick: () => void }) => (
  <ThemeOverride mode="light">
    <Button
      icon="arrow-right"
      aria-label="Search"
      disabled={false}
      type="button"
      onClick={onClick}
    />
  </ThemeOverride>
));

SearchButton.displayName = "SearchButton";

export const FinderSearchBar: React.FC<ContentComponentProps<FinderSearchBarContent>> = ({
  clientGeolocationErrorMessages,
  detectLocationDropdownLabel,
  invalidLocationErrorMessage,
  queryParams,
  redirectPath,
  searchInputLabel
}) => {
  const { expandedMode } = useExperience();
  const [searchValue, searchValueSet] = React.useState("");
  const router = useRouter();
  const formRef = React.useRef<HTMLFormElement>(null);
  const optionSelected = React.useRef<DropdownOption | null>(null);

  // We reroute the location selection through the HTML Form element's `submit` event
  // listener to ensure Amplitude picks up the `Form Submit` event
  const handleSubmit = React.useCallback(() => {
    formRef.current?.dispatchEvent(
      new Event("submit", {
        bubbles: true,
        cancelable: true
      })
    );
  }, []);

  const handleSubmitForm = React.useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();

      if (optionSelected.current) {
        const queryParamsAll = new URLSearchParams({
          ...queryParams,
          placeId: String(optionSelected.current.value)
        });

        router.push(`${redirectPath}?${queryParamsAll.toString()}`);
      }
    },
    [queryParams, redirectPath, router]
  );

  const searchOptionSelect = React.useCallback(
    (option: DropdownOption) => {
      optionSelected.current = option;
      handleSubmit();
    },
    [handleSubmit]
  );

  const {
    handleOnBlur,
    handleSearchChange,
    handleSearchSubmit,
    handleSearchSuggestionClick,
    searchSuggestionsList,
    searchError
  } = useLocationSearch({
    clientGeolocationErrorMessages,
    detectLocationDropdownLabel,
    invalidLocationErrorMessage,
    searchOptionSelect,
    searchValue,
    searchValueSet
  });

  return (
    <Box
      ref={formRef}
      as="form"
      data-track="finder-search-bar"
      sx={{ position: "relative" }}
      onSubmit={handleSubmitForm}
    >
      <TextField
        error={searchError}
        dropdownOptions={searchSuggestionsList}
        searchIcon={() => <SearchButton onClick={handleSearchSubmit} />}
        label={searchInputLabel}
        type="select"
        value={searchValue}
        showMenuOverride={expandedMode}
        onChange={handleSearchChange}
        onEnter={handleSearchSubmit}
        onSelectOption={handleSearchSuggestionClick}
        onBlur={handleOnBlur}
      />
    </Box>
  );
};
